import React from 'react';
import { useFileUpload } from '../../hooks/useFileUpload';
import NumImagesSelect from '../../../../components/NumImagesSelect';
import UniversalSubmitButton from '../../../../components/UniversalSubmitButton';
import FileUploader from '../../../../components/FileUploader';
import CustomSelect from '../../../../components/CustomSelect';
import styles from './ClothingTryOnTab.module.css';

// Image size options
const IMAGE_SIZE_OPTIONS = [
    '', 'portrait_4_3', 'portrait_16_9', 'landscape_4_3', 'landscape_16_9', 'square', 'square_hd'
];
const IMAGE_SIZE_LABELS = {
    '': 'Auto (from image)',
    'portrait_4_3': 'Portrait 3:4',
    'portrait_16_9': 'Portrait 9:16',
    'landscape_4_3': 'Landscape 4:3',
    'landscape_16_9': 'Landscape 16:9',
    'square': 'Square',
    'square_hd': 'Square HD',
};

const ClothingTryOnTab = ({ 
    onSubmit, 
    isSubmitting, 
    error,
    costs,
    modelImageFromGallery,
    onClearGalleryModel
}) => {
    const modelUpload = useFileUpload();
    const garmentUpload = useFileUpload();
    const [numImages, setNumImages] = React.useState(2);
    const [prompt, setPrompt] = React.useState('');
    const [imageSize, setImageSize] = React.useState('');

    const hasModelImage = modelImageFromGallery || modelUpload.file;
    const hasGarmentImage = garmentUpload.file;

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!hasModelImage || !hasGarmentImage) {
            return;
        }

        const formData = new FormData();
        
        if (modelImageFromGallery && modelImageFromGallery.signed_url) {
            formData.append('model_image_url', modelImageFromGallery.signed_url);
            if (modelImageFromGallery.aspect_ratio) {
                formData.append('model_aspect_ratio', modelImageFromGallery.aspect_ratio);
            }
        } else if (modelUpload.file) {
            formData.append('model_image', modelUpload.file);
        }

        formData.append('garment_image', garmentUpload.file);
        formData.append('num_images', numImages);
        
        // Add prompt if provided
        if (prompt.trim()) {
            formData.append('prompt', prompt.trim());
        }
        
        // Add image_size if selected (empty = auto from input image)
        if (imageSize) {
            formData.append('image_size', imageSize);
        }

        onSubmit(formData, modelUpload.aspectRatio || modelImageFromGallery?.aspect_ratio);

        // Reset after submission
        modelUpload.reset();
        garmentUpload.reset();
        setPrompt('');
        setImageSize('');
        if (onClearGalleryModel) {
            onClearGalleryModel();
        }
    };

    const handleClearModel = () => {
        modelUpload.reset();
        if (onClearGalleryModel) {
            onClearGalleryModel();
        }
    };

    return (
        <form onSubmit={handleSubmit} className={styles.tryOnForm}>
            <h3>Virtual Try-On</h3>
            <p className={styles.description}>
                Upload a model photo and a garment image to see how it looks.
            </p>

            {/* Model Image */}
            {modelImageFromGallery ? (
                <div>
                    <label>Model Photo:</label>
                    <div className={styles.galleryImageContainer}>
                        <img 
                            src={modelImageFromGallery.signed_url} 
                            alt="Model from gallery" 
                            className={styles.previewImage}
                        />
                        <div className={styles.galleryImageInfo}>
                            <span>✓ From Gallery</span>
                            <button 
                                type="button"
                                onClick={handleClearModel}
                                className={styles.clearButton}
                            >
                                Change Image
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <FileUploader
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    multiple={false}
                    maxSizeMB={10}
                    onChange={(file) => {
                        if (file) {
                            const fakeEvent = { target: { files: [file] } };
                            modelUpload.handleFileChange(fakeEvent);
                        }
                    }}
                    disabled={isSubmitting}
                    label="Model Photo"
                    showPreview={true}
                />
            )}

            {/* Garment Image */}
            <FileUploader
                accept="image/png,image/jpeg,image/jpg,image/webp"
                multiple={false}
                maxSizeMB={10}
                onChange={(file) => {
                    if (file) {
                        const fakeEvent = { target: { files: [file] } };
                        garmentUpload.handleFileChange(fakeEvent);
                    }
                }}
                disabled={isSubmitting}
                label="Garment Photo"
                showPreview={true}
            />

            {/* Prompt (optional) */}
            <div className={styles.promptSection}>
                <label htmlFor="tryOnPrompt">Description (optional):</label>
                <textarea
                    id="tryOnPrompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe how you want the try-on to look, e.g. 'A person wearing a stylish jacket, casual look'"
                    className={styles.promptInput}
                    disabled={isSubmitting}
                    rows={3}
                    maxLength={500}
                />
                <span className={styles.charCount}>{prompt.length}/500</span>
            </div>

            {/* Image Size */}
            <div>
                <label>Output Size:</label>
                <CustomSelect
                    value={imageSize}
                    onChange={setImageSize}
                    options={IMAGE_SIZE_OPTIONS}
                    labels={IMAGE_SIZE_LABELS}
                    disabled={isSubmitting}
                />
            </div>

            {/* Number of Images */}
            <div>
                <label>Number of Images:</label>
                <NumImagesSelect
                    value={numImages}
                    onChange={setNumImages}
                    disabled={isSubmitting}
                />
            </div>

            {/* Error */}
            {error && <div className={styles.errorMessage}>{error}</div>}

            {/* Info */}
            <div className={styles.infoBox}>
                <p>
                    💡 <strong>Tips:</strong>
                </p>
                <ul>
                    <li>Use clear, front-facing photos for best results</li>
                    <li>Garment image can be flat-lay or on model</li>
                    <li>Add a description for more control over the result</li>
                </ul>
            </div>

            <UniversalSubmitButton
                isSubmitting={isSubmitting}
                baseCost={costs?.virtual_try_on}
                quantity={numImages}
                disabled={!hasModelImage || !hasGarmentImage}
            />
        </form>
    );
};

export default ClothingTryOnTab;
